import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@material-ui/core";
import moment from "moment";

import { useEffect, useState } from "react";
import { BiTrash } from "react-icons/bi";
import { FiEdit2 } from "react-icons/fi";
import { IoMdDownload } from "react-icons/io";
import { LuArrowDown, LuArrowDownUp, LuArrowUp } from "react-icons/lu";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import CustomConfirmModal from "../../../components/Common/CustomConfirmModal";
import CustomPagination from "../../../components/Common/CustomPagination";
import { getCurrentUserId, getCurrentUserOrganizationId, toastError, toastSuccess } from "../../../utils";
import StatementofFactsModel from "./StatementofFactsModel";
import { createElectricSof, createElectricSofPdf, deleteElectricSof, getElectricSof, updateElectricSof } from "./action";

const formatDateOrDash = (v) => (v && moment(v).isValid() ? moment(v).format("DD-MM-YYYY") : "-");

const headers = [
    "S.No",
    "Vessel Name",
    "Cargo Grade",
    "B/L Date",
    "Voyage No",
    "Charterer",
    "Laytime for the Port",
    "Operation Type",
    "Cargo Quantity (MT)",
    "Shipment ID",
    "Action"
];

const intialData = {
    events: [""],
    userFields: [""],
    remarks: {
        general: [""],
        shippers: [""],
        shipOwners: [""],
    },
    sof_authors: [
        {
            id: 1,
            name: "",
            designation: "",
            details: "",
        },
    ],
    vesselData: {
        vesselName: "",
        grade: "",
        billOfLadingDate: "",
        voyageNo: "",
        charterer: "",
        port: "",
        operation: "",
        cargoQty: "",
        shipmentId: "",
        timeZone: "",
    },
    userId: getCurrentUserId(),
    organizationId: getCurrentUserOrganizationId(),
};

const intialModelState = {
    open: false,
    data: intialData,
    type: "create",
};

const ElectricSof = (props) => {
    const { setLoader = () => { } } = props;
    const [data, setData] = useState([]);
    const [openModal, setOpenModal] = useState({ ...intialModelState });
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        offset: 1,
        limit: 50,
        totalPages: 0,
        totalItems: 0,
        sortBy: "createdAt",
        sortDirection: "desc",
    });

    const fetchData = async () => {
        setLoading(true);
        setLoader(true);
        try {
            const response = await getElectricSof({
                offset: pagination.offset || 1,
                limit: pagination.limit || 50,
                sortBy: pagination.sortBy,
                sortDirection: pagination.sortDirection,
            });
            if (!response) {
                throw new Error("Failed to fetch data");
            }

            setData(response.data || []);
            setPagination((prev) => ({
                ...prev,
                totalPages: response.pages,
                totalItems: response.count,
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [pagination.offset, pagination.limit, pagination.sortBy, pagination.sortDirection]);

    const onChangePagination = (type, key) => {
        setPagination((prev) => {
            const currentPage = prev.offset || 1;
            const limit = prev.limit || 10;

            let newOffset = currentPage;

            if (type === "first") {
                newOffset = 1;
            } else if (type === "dec" && currentPage > 1) {
                newOffset = currentPage - 1;
            } else if (type === "inc" && currentPage * limit < pagination.totalItems) {
                newOffset = currentPage + 1;
            }

            return {
                ...prev,
                offset: newOffset,
            };
        });
    };

    const handleSortClick = (sortKey) => {
        let newSortDirection = "asc";
        if (pagination.sortBy === sortKey) {
            if (pagination.sortDirection === "asc") newSortDirection = "desc";
            else if (pagination.sortDirection === "desc") {
                newSortDirection = sortKey === "createdAt" ? "asc" : "";
            }
        } else {
            newSortDirection = sortKey === "createdAt" ? "desc" : "asc";
        }

        let nextSortBy = sortKey;
        if (!newSortDirection) {
            nextSortBy = "createdAt";
            newSortDirection = "desc";
        }

        setPagination((prev) => ({
            ...prev,
            offset: 1,
            sortBy: nextSortBy,
            sortDirection: newSortDirection,
        }));
    };

    const handleOpenModal = (data) => {
        if (data) {
            setOpenModal((prev) => {
                return {
                    open: true,
                    type: "edit",
                    data: data,
                };
            });
        } else {
            setOpenModal((prev) => {
                return {
                    open: true,
                    type: "create",
                    data: intialData,
                };
            });
        }
    }

    const [showUpdateWarning, setShowUpdateWarning] = useState(false);
    const [openOfflineInfo, setOpenOfflineInfo] = useState(false);

    const handleCreateSof = async () => {
        setLoading(true);
        try {
            let response
            if (openModal.type === "create") {
                response = await createElectricSof(openModal.data);
            } else {
                const { _id, ...data } = openModal.data;
                response = await updateElectricSof({ _id }, data);
            }
            if (!response) {
                toastError(`Failed to ${openModal.type === "create" ? "create" : "update"} data`);
            }
            setOpenModal(intialModelState);
            toastSuccess(`Data ${openModal.type === "create" ? "created" : "updated"} successfully`);
            fetchData();
        } catch (err) {
            toastError(err.message);
        } finally {
            setShowUpdateWarning(false);
            setLoading(false);
        }
    }

    const handleDownloadPdf = async () => {
        setLoading(true);
        try {
            let response
            response = await createElectricSofPdf(openModal.data);
            if (!response) {
                toastError(`Failed to create data`);
            }
            return response.data || response;
        } catch (err) {
            toastError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            let response
            response = await deleteElectricSof({ _id: id });
            if (!response) {
                toastError(`Failed to delete data`);
            }
            toastSuccess(`Data deleted successfully`);
            fetchData();
        } catch (err) {
            toastError(err.message);
        } finally {
            setShowDeleteWarning({
                isOpen: false,
                id: null,
            });
            setLoading(false);
        }
    };

    function DownloadIndexHtml() {
        const handleDownload = () => {
            const link = document.createElement('a');
            link.href = '/htmlForms/sof.html';
            link.download = `${Date.now()}_sof.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        return (
            <>
        <button
          onClick={() => setOpenOfflineInfo(true)}
          className="h-7 px-3 bg-[#313C4C] hover:bg-[#1e2632] text-white text-[11px] font-medium rounded-none flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
        >
          <IoMdDownload size={13} />
          <span>Offline Form</span>
        </button>
        <CustomAddModal
          open={openOfflineInfo}
          handleClose={() => setOpenOfflineInfo(false)}
          onClose={() => setOpenOfflineInfo(false)}
          title="Offline Statement of Facts Form"
          saveLabel="Download"
          onSubmitConfirm={() => { handleDownload(); setOpenOfflineInfo(false); }}
        >
          <div className="text-[12px] text-slate-700">
            <div className="border border-slate-200 bg-slate-50 px-3 py-3">
              <div className="font-semibold text-slate-900 mb-1">What is this file?</div>
              <p>A blank Statement of Facts form saved as an HTML file. It opens in any web browser, so you can fill it in on a computer without logging in to this app, then bring the filled form back into this page.</p>
            </div>
            <div className="font-semibold text-slate-900 mt-4 mb-2">How to use it</div>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Click <strong>Download</strong> below to save the offline form (.html file).</li>
              <li>Open the downloaded file in your browser and fill in the details.</li>
              <li>In the form, click <strong>Download HTML</strong> to save your filled-in copy.</li>
            </ol>
            <div className="font-semibold text-slate-900 mt-4 mb-2">Where can I import the filled form?</div>
            <ol className="list-decimal pl-5 space-y-2">
              <li>On this page, click <strong>Create Statement of Facts</strong> (top right).</li>
              <li>In the window that opens, click <strong>Upload HTML</strong> at the top.</li>
              <li>Click <strong>Choose completed HTML file</strong> and select the file you saved in the previous step. The form fields are filled in from it.</li>
            </ol>
            <p className="mt-3 text-slate-600">The same <strong>Upload HTML</strong> option is also available in the Statement of Facts panel of an operation (Operations page, open an operation, then <strong>View</strong> in the Statement of Facts column).</p>
          </div>
        </CustomAddModal>
      </>
        );
    }

    const [showDeleteWarning, setShowDeleteWarning] = useState({
        isOpen: false,
        id: null,
    });

    const showDeleteWarningModel = (id) => {
        setShowDeleteWarning({
            isOpen: true,
            id,
        });
    }

    return (
        <div className="min-h-[calc(100vh-110px)] w-full">
            <div className="mb-2 flex gap-2 justify-end items-center">
                {DownloadIndexHtml()}
                <button
                    className="h-7 px-3 bg-[#313C4C] hover:bg-[#1e2632] text-white text-[11px] font-medium rounded-none flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
                    onClick={() => handleOpenModal()}
                >
                    Create Statement of Facts
                </button>
            </div>

            <TableContainer component={Paper} className="!rounded-none !shadow-sm">
                {pagination?.totalItems > pagination?.limit && (
                    <CustomPagination
                        onChangePagination={onChangePagination}
                        filters={pagination}
                        totalPage={pagination?.totalItems}
                    />
                )}
                {/* Always render table */}
                <Table className="universal-table">
                    <TableHead className="bg-[#313C4C]">
                        <TableRow>
                            {headers.map((header) => {
                                const sortMap = {
                                    "Vessel Name": "vesselData.vesselName",
                                    "Cargo Grade": "vesselData.grade",
                                    "B/L Date": "vesselData.billOfLadingDate",
                                    "Voyage No": "vesselData.voyageNo",
                                    "Charterer": "vesselData.charterer",
                                    "Laytime for the Port": "vesselData.port",
                                    "Operation Type": "vesselData.operation",
                                    "Cargo Quantity (MT)": "vesselData.cargoQty",
                                    "Shipment ID": "vesselData.shipmentId",
                                };
                                const sortKey = sortMap[header];
                                return (
                                    <TableCell
                                        key={header}
                                        className={`text-white py-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap ${headers.indexOf(header) === headers.length - 1
                                            ? "border-r-0"
                                            : ""
                                            } ${sortKey ? 'cursor-pointer' : ''}`}
                                        onClick={() => sortKey ? handleSortClick(sortKey) : undefined}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            {header}
                                            {sortKey && (
                                                <div style={{ color: pagination.sortBy === sortKey ? "#fff" : "rgba(255,255,255,0.4)", display: "flex", alignItems: "center" }}>
                                                    {pagination.sortBy === sortKey && pagination.sortDirection === "desc" ? <LuArrowDown size={13} /> : pagination.sortBy === sortKey && pagination.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={headers.length} align="center" className="!bg-white">
                                    <Box display="flex" justifyContent="center" alignItems="center" height="440px">
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : data.length > 0 ? (
                            data.map((row, index) => (
                                <TableRow
                                    key={index}
                                    className="h-7 cursor-pointer hover:bg-gray-50"
                                    onDoubleClick={() => handleOpenModal(row)}
                                >
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{(pagination.offset - 1) * pagination.limit + index + 1}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{row?.vesselData?.vesselName}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{row?.vesselData?.grade}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{formatDateOrDash(row?.vesselData?.billOfLadingDate)}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{row?.vesselData?.voyageNo}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{row?.vesselData?.charterer}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{row?.vesselData?.port}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{row?.vesselData?.operation}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{row?.vesselData?.cargoQty}</TableCell>
                                    <TableCell className="p-0 px-2 text-[11px] font-medium border-gray-400">{row?.vesselData?.shipmentId}</TableCell>
                                    <TableCell className="p-0 px-2">
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => handleOpenModal(row)}
                                                className="p-1 text-[#313C4C] hover:text-[#1e2632] transition cursor-pointer"
                                                title="Edit"
                                            >
                                                <FiEdit2 size={15} style={{ display: "block" }} />
                                            </button>
                                            <button
                                                onClick={() => showDeleteWarningModel(row._id)}
                                                className="p-1 text-rose-600 hover:text-rose-800 transition cursor-pointer"
                                                title="Delete"
                                            >
                                                <BiTrash className="table-action-delete-icon" style={{ width: "15px", height: "15px", minWidth: "15px", minHeight: "15px" }} />
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={headers.length} className="text-center">
                                    No data available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {pagination?.totalItems > pagination?.limit && (
                    <CustomPagination
                        onChangePagination={onChangePagination}
                        filters={pagination}
                        totalPage={pagination?.totalItems}
                    />
                )}
            </TableContainer>

            <CustomAddModal
                title={
                    openModal.type === "create"
                        ? "Create Statement of Facts"
                        : "Edit Statement of Facts"
                }
                open={openModal.open}
                maxWidth="md"
                saveLabel={openModal.type === "create" ? "Create" : "Update"}
                onSubmitConfirm={() => {
                    if (openModal.type === "create") {
                        handleCreateSof();
                    } else {
                        setShowUpdateWarning(true);
                    }
                }}
                handleClose={() => setOpenModal((prev) => ({ ...prev, open: false }))}
            >
                <StatementofFactsModel state={openModal.data} setState={setOpenModal} onDownloadElectricPdf={handleDownloadPdf} />
            </CustomAddModal>
            <CustomConfirmModal
                open={showDeleteWarning.isOpen}
                handleClose={() => setShowDeleteWarning((prev) => ({ ...prev, isOpen: false }))}
                title="Delete Statement of Facts"
                message="Are you sure you want to delete this statement of facts?"
                type="warning"
                confirmText="DELETE"
                cancelText="Cancel"
                note="This action cannot be undone."
                onSubmitConfirm={() => handleDelete(showDeleteWarning.id)}
            />
            <CustomConfirmModal
                open={showUpdateWarning}
                handleClose={() => setShowUpdateWarning(false)}
                title="Update Statement of Facts"
                message="Are you sure you want to update this statement of facts?"
                type="warning"
                confirmText="UPDATE"
                cancelText="Cancel"
                note="Please double check all details before updating this statement of facts. This action cannot be undone."
                onSubmitConfirm={() => handleCreateSof()}
            />
        </div >
    );
};

export default ElectricSof;
